import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  message,
  Image,
  UploadFile,
} from "antd";
import { CloudUploadOutlined } from "@ant-design/icons";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { getBase64 } from "@/pages/Personal/util";
import { IS_MOBILE } from "@/utils/tool";
import FormItem from "antd/es/form/FormItem";
import useLanguage from "@/hooks/useLanguage";
import dayjs from "dayjs";
const { Dragger } = Upload;
import { transferConfirmation } from "@/api/personal";
import type { RcFile } from "antd/es/upload/interface";
import type { GetProp, UploadProps } from "antd";
import { SvgIcon } from "@/components/SvgIcon";
type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];
type LayoutType = Parameters<typeof Form>[0]["layout"];

const limitSize = 5 * 1024 * 1024;

export interface TransferConfirmationProps {
  hiddenModal: () => void;
}

const TransferConfirmation: React.FC<TransferConfirmationProps> = ({
  hiddenModal,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [formLayout, setFormLayout] = useState<LayoutType>(
    language === "zh" ? "horizontal" : "vertical"
  );

  const formItemLayout =
    formLayout === "horizontal"
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 20 },
        }
      : null;

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({
    file: rawFile,
    fileList: newFileList,
  }) => {
    if ((rawFile as FileType).size > limitSize) {
      form.setFieldsValue({
        file: null,
      });
      return false;
    }
    setFileList(newFileList);
  };

  const normFile = (e: any) => {
    if (e?.fileList.length) {
      return e.file;
    }
  };

  const uploadButton = (
    <div className="upload-box">
      <CloudUploadOutlined
        width={32}
        height={32}
        style={{ color: "var(--kx-fill-4)" }}
      />

      <span className="ant-upload-text">{t("选择文件或拖入文件到此区域")}</span>
      <span className="ant-upload-hint">
        {t("上传转账凭证照片或截屏，大小不超过5M，PNG或JPG格式")}
      </span>
    </div>
  );

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        const file = values.file as FileType;
        if (file.size > limitSize) {
          message.warning(t("文件大小不能超过5M"));
          form.setFieldsValue({
            file: null,
          });
          return;
        }
        // 处理接口
        const formData = new FormData();

        formData.append("bank_transfer_number", values.bank_transfer_number);
        formData.append("pay_money", values.pay_money);
        formData.append("pay_user_info", values.pay_user_info);
        formData.append("time", values.time.unix());
        formData.append("transaction_id", values.transaction_id);
        formData.append("file", values.file);

        const result = await transferConfirmation(formData);
        if (result.code !== 0) {
          message.error(result.msg);
        } else {
          message.success(t("已确认"));
        }
        hiddenModal();
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <Modal
      open
      wrapClassName="modal-wrapper"
      onCancel={hiddenModal}
      footer={
        <>
          <button className="cancel" onClick={hiddenModal}>
            {t("取消")}
          </button>
          <button className="done" onClick={handleSubmit}>
            {t("确认已支付")}
          </button>
        </>
      }
      title={t("线下汇款确认")}
      centered
      maskClosable={false}
    >
      <Form
        {...formItemLayout}
        layout={formLayout}
        form={form}
        style={{ width: "90%" }}
        className="invoice-form"
        labelAlign="left"
        colon={false}
      >
        <Form.Item
          name="transaction_id"
          label={t("支付交易号")}
          rules={[{ required: true, message: t("请输入必填项") }]}
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
        <Form.Item
          name="pay_money"
          label={t("付款金额")}
          rules={[{ required: true, message: t("请输入必填项") }]}
        >
          <Input placeholder={t("请输入")} addonBefore="¥" />
        </Form.Item>
        <Form.Item
          name="time"
          label={t("到账时间")}
          rules={[{ required: true, message: t("请输入必填项") }]}
        >
          <DatePicker
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: "100%" }}
            showTime={{ defaultValue: dayjs("00:00:00", "HH:mm:ss") }}
          />
        </Form.Item>
        <Form.Item
          name="bank_transfer_number"
          label={t("转账流水号")}
          rules={[{ required: true, message: t("请输入必填项") }]}
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
        <Form.Item
          name="pay_user_info"
          label={t("付款人账号")}
          rules={[{ required: true, message: t("请输入必填项") }]}
        >
          <Input placeholder={t("请输入")} />
        </Form.Item>
        <Form.Item
          name="file"
          label={t("转账凭证")}
          rules={[{ required: true, message: t("请输入必填项") }]}
          //   valuePropName="fileList"
          getValueFromEvent={normFile}
        >
          <Dragger
            listType="picture-card"
            fileList={fileList}
            name="file"
            accept=".jpg,.png"
            className={
              fileList?.length ? "file-box file-exist" : "file-box file-empty"
            }
            beforeUpload={(file: File) => {
              if (file.size > limitSize) {
                message.warning(t("文件大小不能超过5M"));
                form.setFieldsValue({
                  file: null,
                });
              }

              return false;
            }}
            onPreview={handlePreview}
            onChange={handleChange}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Dragger>
        </Form.Item>
      </Form>
      <Image
        wrapperStyle={{ display: "none" }}
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible),
          afterOpenChange: (visible) => !visible && setPreviewImage(""),
        }}
        src={previewImage}
      />
    </Modal>
  );
};

export default TransferConfirmation;
